import {
  Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { clinician } from '@/api/endpoints';
import type { ClinicianProfile } from '@/types/medical';

/**
 * Clinician profile / settings.
 *
 * Editable fields are intentionally minimal — most "trust" facts (licenses,
 * credentials) live elsewhere. The clinician edits public-facing data:
 * display name, bio, languages, modality offering, and pricing only when
 * `self_employed=true`.
 */

export function ClinicianProfilePage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'me'],
    queryFn:  clinician.me.get,
  });

  return (
    <>
      <PageHeader title="My profile" />
      <DataState query={q}>
        {(me) => <ProfileForm initial={me} />}
      </DataState>
    </>
  );
}

function ProfileForm({ initial }: { initial: ClinicianProfile }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [displayName, setDisplayName] = useState(initial.display_name);
  const [bio, setBio] = useState(initial.bio);
  const [languages, setLanguages] = useState((initial.languages ?? []).join(', '));
  const [offersInPerson, setOffersInPerson] = useState(initial.offers_in_person);
  const [offersVideo, setOffersVideo] = useState(initial.offers_video);
  const [offersPhone, setOffersPhone] = useState(initial.offers_phone);
  const [offersAsync, setOffersAsync] = useState(initial.offers_async);
  const [acceptsNew, setAcceptsNew] = useState(initial.accepts_new_patients);
  const [isListed, setIsListed] = useState(initial.is_listed);

  // Keep form in sync if a parallel mutation refreshes the source.
  useEffect(() => {
    setDisplayName(initial.display_name);
    setBio(initial.bio);
    setLanguages((initial.languages ?? []).join(', '));
    setOffersInPerson(initial.offers_in_person);
    setOffersVideo(initial.offers_video);
    setOffersPhone(initial.offers_phone);
    setOffersAsync(initial.offers_async);
    setAcceptsNew(initial.accepts_new_patients);
    setIsListed(initial.is_listed);
  }, [initial.id]);

  const saveMut = useApiMutation({
    mutationFn: (patch: Partial<ClinicianProfile>) => clinician.me.update(patch),
    onSuccess: () => {
      toast.success('Profile saved.');
      qc.invalidateQueries({ queryKey: ['clinician', 'me'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    saveMut.mutate({
      display_name: displayName,
      bio,
      languages: languages.split(',').map((s) => s.trim()).filter(Boolean),
      offers_in_person: offersInPerson,
      offers_video: offersVideo,
      offers_phone: offersPhone,
      offers_async: offersAsync,
      accepts_new_patients: acceptsNew,
      is_listed: isListed,
    });
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Public profile
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="Languages"
                value={languages}
                onChange={(e) => setLanguages(e.target.value)}
                helperText="Comma-separated. e.g. en, pl, de"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={4} label="Bio"
                value={bio} onChange={(e) => setBio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {initial.specialties.map((s) => (
                  <Chip key={s.id} label={s.name} variant="outlined" />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Specialties are managed via the practice admin.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Modalities you offer
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Switch checked={offersInPerson}
                                                 onChange={(_, v) => setOffersInPerson(v)} />}
                                label="In person" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Switch checked={offersVideo}
                                                 onChange={(_, v) => setOffersVideo(v)} />}
                                label="Video" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Switch checked={offersPhone}
                                                 onChange={(_, v) => setOffersPhone(v)} />}
                                label="Phone" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel control={<Switch checked={offersAsync}
                                                 onChange={(_, v) => setOffersAsync(v)} />}
                                label="Async chat" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Visibility
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel control={<Switch checked={isListed}
                                               onChange={(_, v) => setIsListed(v)} />}
                              label="Listed in marketplace search" />
            <FormControlLabel control={<Switch checked={acceptsNew}
                                               onChange={(_, v) => setAcceptsNew(v)} />}
                              label="Accepting new patients" />
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={submit} disabled={saveMut.isPending}>
          Save profile
        </Button>
      </Box>
    </Stack>
  );
}
